'use client'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { AuthContextOrgListItem } from '@/hooks/use-auth-context'

export function toolWorkspaceSelectionRequired({
  workspaceCount,
  selectedWorkspaceId,
  lockedWorkspaceId,
}: {
  workspaceCount: number
  selectedWorkspaceId: string | null
  lockedWorkspaceId?: string
}): boolean {
  return workspaceCount > 1 && !lockedWorkspaceId && !selectedWorkspaceId
}

/** Shared billing-owner picker for public Fal and Seedance studios.
 *
 * An org-scoped mount supplies `lockedWorkspaceId`; it deliberately renders no
 * picker because the URL already owns the billing decision. Public multi-org
 * mounts start empty so the visitor makes an explicit choice for this run.
 */
export function ToolWorkspacePicker({
  workspaces,
  selectedWorkspaceId,
  lockedWorkspaceId,
  disabled = false,
  onSelect,
}: {
  workspaces: AuthContextOrgListItem[]
  selectedWorkspaceId: string | null
  lockedWorkspaceId?: string
  disabled?: boolean
  onSelect: (workspaceId: string) => void
}) {
  if (lockedWorkspaceId || workspaces.length <= 1) return null

  return (
    <div className="border-border bg-muted/30 mb-5 rounded-xl border p-4">
      <label
        className="text-muted-foreground text-xs font-medium"
        htmlFor="tool-workspace"
      >
        Workspace for this run
      </label>
      <Select
        value={selectedWorkspaceId ?? ''}
        disabled={disabled}
        onValueChange={onSelect}
      >
        <SelectTrigger id="tool-workspace" className="mt-2 w-full">
          <SelectValue placeholder="Choose a workspace" />
        </SelectTrigger>
        <SelectContent>
          {workspaces.map(workspace => (
            <SelectItem key={workspace.id} value={workspace.id}>
              {workspace.name ?? workspace.slug}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground mt-2 text-xs">
        The selected workspace provides the plan and credits for this run.
      </p>
    </div>
  )
}
